/**
 * @owner @Appboy/intelligence
 */

import type { I18nT } from "@lib/dashboardI18n";

import { rulesetContexts } from "./types";
import type { RulesetContext, RulesetContextCopy } from "./types";

/**
 * What each evaluation context means to every surface that mentions it: the
 * rules UI, Operator's preset descriptions, its absence clauses and its
 * category guidance.
 *
 * Exhaustive over `RulesetContext`, so a context added to
 * `RulesetPresets::Contexts` fails to compile until every surface has decided
 * what to say about it — which is the point of keeping them in one table rather
 * than one map per consumer.
 */
export const rulesetContextCopy: Record<RulesetContext, RulesetContextCopy> = {
  [rulesetContexts.LIQUID_REFERENCE]: {
    ruleListNote: (t: I18nT<"custom-agent-form">) =>
      t("liquid-context-help", "Braze Liquid templating documentation"),
    operatorDescription: () =>
      "**Braze Liquid reference documentation** is injected so the rules can reason about Liquid correctly",
    // Injected for both Standard kinds wherever the preset is offered, so there
    // is no asymmetry to explain.
    absenceNote: null,
    ruleTheme: "Liquid/personalization rules",
  },
  [rulesetContexts.ACCESSIBILITY_SCAN]: {
    ruleListNote: (t: I18nT<"custom-agent-form">) =>
      t("a11y-context-help", "WCAG 2.1 Level AA accessibility scan results"),
    operatorDescription: (summaryNoun) =>
      `An **accessibility-enriched ${summaryNoun}** is provided so the rules can check a11y/deliverability concerns`,
    absenceNote: "no automated accessibility scan runs for this Standard kind",
    ruleTheme: "legal/spam/accessibility rules",
  },
  [rulesetContexts.BRAND_GUIDELINES]: {
    // Shown as its own section with a guideline picker, not as a note.
    ruleListNote: null,
    operatorDescription: () =>
      "**Brand guidelines** (when one is attached to the agent) are injected so the rules can check on-brand voice",
    // Depends on whether a guideline is attached to the agent rather than on the
    // Standard kind, and the rules UI says so with its own picker.
    absenceNote: null,
    ruleTheme: "Brand-voice/tone rules",
  },
  [rulesetContexts.MESSAGE_IMAGES]: {
    // Attached only when the images flipper is on and the model can take images,
    // so the rules UI makes no promise about them.
    ruleListNote: null,
    operatorDescription: () => "The message's **inline images** are attached so visual rules can be checked",
    // The instructions describe image attachment separately per Standard kind,
    // because it also turns on whether the agent's model accepts images.
    absenceNote: null,
    // Every ruleset in a scope receives these, so the category is irrelevant.
    ruleTheme: null,
  },
};
